import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchBlogPostById } from '../services/newsService';
import type { NewsPost } from '../types/news';
import NewsImageCarousel from '../components/NewsImageCarousel';
import CommentSection from '../components/CommentSection';
import LikeButton from '../components/LikeButton';

const BlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<NewsPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    fetchBlogPostById(id).then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) {
        setNotFound(true);
      } else {
        setPost(data);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (loading) {
    return (
      <div className="bg-[#080808] text-white min-h-screen flex items-center justify-center">
        <p style={{ color: 'rgba(255,255,255,0.4)' }}>Laster innlegg...</p>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="bg-[#080808] text-white min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-5xl font-bold text-white mb-4">404</h1>
          <p className="text-xl text-gray-400 mb-8">Fant ikke innlegget</p>
          <Link to="/blog" className="btn-primary py-3 px-8 font-bold">
            Tilbake til bloggen
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#080808] text-white min-h-screen section-padding overflow-hidden">
      <div className="container-responsive max-w-4xl">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 link-secondary text-sm mb-8 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z" />
          </svg>
          Tilbake til bloggen
        </button>

        <article className="card-glow overflow-hidden">
          {post.image_urls.length > 0 && (
            <NewsImageCarousel images={post.image_urls} alt={post.title} />
          )}

          <div className="p-6 sm:p-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight">
              {post.title}
            </h1>
            <p className="text-sm text-gray-500 mb-8">
              {formatDate(post.created_at)}
              {post.author_display_name && (
                <>
                  {' · '}
                  <Link
                    to={`/users/${post.author_display_name}`}
                    className="link-primary hover:no-underline font-medium"
                  >
                    {post.author_display_name}
                  </Link>
                </>
              )}
              {post.updated_at && post.updated_at !== post.created_at && (
                <span className="ml-3 text-gray-600">(redigert {formatDate(post.updated_at)})</span>
              )}
            </p>

            <div className="text-gray-300 leading-relaxed whitespace-pre-wrap text-base sm:text-lg">
              {post.content}
            </div>

            <div className="mt-6 flex items-center">
              <LikeButton postId={post.id} />
            </div>

            <div id="comments">
              <CommentSection postId={post.id} />
            </div>
          </div>
        </article>

        <div className="mt-8 text-center">
          <Link to="/blog" className="btn-secondary py-3 px-8 font-bold">
            ← Tilbake til bloggen
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
